import { DataTable } from '@/components'
import { useActivityTypeLabels } from '@/modules/activities/use-activity-type-labels'
import { format } from '@/utils'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { useMemo } from 'react'

export interface UnpaidActivity {
  id: string
  activityType: string
  datetimeStart: string
  datetimeEnd: string
  excludeFromSalaryCalculation: boolean
  excludeComment?: string | null
}

interface UnpaidActivitiesTableProps {
  activities: UnpaidActivity[]
}

/**
 * The activities a manager has marked as not paid, with the comment explaining
 * why. Renders nothing when every activity in the work period is paid.
 */
export const UnpaidActivitiesTable: React.FC<UnpaidActivitiesTableProps> = ({
  activities,
}) => {
  const translations = useTranslate({
    unpaidActivities: 'salary.unpaid-activities',
    activity: 'common.activity',
    time: 'common.time',
    comment: 'common.comment',
  })
  const activityTypeLabels = useActivityTypeLabels()

  const unpaidActivities = useMemo(
    () =>
      activities
        .filter(activity => activity.excludeFromSalaryCalculation)
        .sort((a, b) => a.datetimeStart.localeCompare(b.datetimeStart)),
    [activities]
  )

  const column = createColumnHelper<UnpaidActivity>()
  const columns = useMemo(
    () => [
      column.accessor('activityType', {
        header: translations.activity,
        cell: info => activityTypeLabels[info.getValue()] ?? info.getValue(),
      }),
      column.display({
        id: 'time',
        header: translations.time,
        meta: { narrow: true },
        cell(info) {
          const activity = info.row.original
          return (
            <span className="whitespace-nowrap">
              {format(parseISO(activity.datetimeStart), 'HH:mm')} -{' '}
              {format(parseISO(activity.datetimeEnd), 'HH:mm')}
            </span>
          )
        },
      }),
      column.accessor('excludeComment', {
        header: translations.comment,
        cell: info => (
          <span className="break-words whitespace-pre-wrap">
            {info.getValue() || '–'}
          </span>
        ),
      }),
    ],
    [
      column,
      activityTypeLabels,
      translations.activity,
      translations.comment,
      translations.time,
    ]
  )

  if (unpaidActivities.length === 0) return null

  return (
    <div className="mt-4">
      <h4 className="mb-1 font-semibold text-neutral-700">
        {translations.unpaidActivities}
      </h4>
      <DataTable columns={columns} data={unpaidActivities} inCard />
    </div>
  )
}
